/**
 * 004 — Derivados ⚙️. Se calculan de `boda.fecha` (del Evento 006) + nombres.
 * NO viven en el payload ni se persisten (diccionario, regla #1: una sola fuente de fecha).
 * Español neutro LATAM (constitución §8).
 */

const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];
const DIAS_SEMANA = [
  'domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado',
];

export interface CountdownParts {
  dias: number;
  horas: number;
  minutos: number;
  segundos: number;
  pasado: boolean;
}

export interface DerivedFields {
  valida: boolean;
  dia: number | null;               // 14
  mes: number | null;               // 1..12
  mesNombre: string;                // "noviembre"
  anio: number | null;              // 2026
  diaSemana: string;                // "sábado"
  fechaNumerica: string;            // "14 · 11 · 2026"
  fechaLarga: string;               // "sábado 14 de noviembre de 2026"
  horaEnLetra: string;              // "5:00 de la tarde"
  monograma: string;                // "A & D"
}

/** Iniciales de los nombres cortos → monograma "A & D". */
export function monograma(brideShort?: string | null, groomShort?: string | null): string {
  const a = (brideShort ?? '').trim().charAt(0).toUpperCase();
  const b = (groomShort ?? '').trim().charAt(0).toUpperCase();
  if (!a && !b) return '';
  return [a, b].filter(Boolean).join(' & ');
}

/** Hora numérica → "5:00 de la tarde" / "10:30 de la mañana" / "8:00 de la noche". */
export function horaEnLetra(d: Date): string {
  const h = d.getHours();
  const m = d.getMinutes();
  const min = m.toString().padStart(2, '0');
  const h12 = h % 12 === 0 ? 12 : h % 12;
  let franja: string;
  if (h < 12) franja = 'de la mañana';
  else if (h < 19) franja = 'de la tarde';
  else franja = 'de la noche';
  return `${h12}:${min} ${franja}`;
}

/** Cuenta regresiva desde `now` (inyectable para tests) hasta la fecha. */
export function countdown(fechaIso: string | null, now: Date = new Date()): CountdownParts {
  const zero: CountdownParts = { dias: 0, horas: 0, minutos: 0, segundos: 0, pasado: true };
  if (!fechaIso) return zero;
  const target = new Date(fechaIso);
  if (isNaN(target.getTime())) return zero;
  const diff = target.getTime() - now.getTime();
  if (diff <= 0) return zero;
  const s = Math.floor(diff / 1000);
  return {
    dias: Math.floor(s / 86400),
    horas: Math.floor((s % 86400) / 3600),
    minutos: Math.floor((s % 3600) / 60),
    segundos: s % 60,
    pasado: false,
  };
}

/**
 * Fecha de calendario TZ-estable desde `boda.fecha`. `wedding_date` es un
 * TIMESTAMPTZ guardado a medianoche UTC (proviene de un date picker date-only),
 * así que `new Date(iso)` + getters locales corría el día al huso del visitante
 * (5-sep se veía 4-sep al oeste de UTC). Tomamos la parte `YYYY-MM-DD` y
 * construimos una fecha LOCAL, sin conversión de huso.
 */
function parseCalendarDate(iso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

/** Todos los derivados de fecha/nombres. `fechaIso` = `boda.fecha` (del Evento). */
export function deriveFields(
  fechaIso: string | null,
  brideShort?: string | null,
  groomShort?: string | null,
): DerivedFields {
  const mono = monograma(brideShort, groomShort);
  const empty: DerivedFields = {
    valida: false,
    dia: null, mes: null, mesNombre: '', anio: null,
    diaSemana: '', fechaNumerica: '', fechaLarga: '', horaEnLetra: '',
    monograma: mono,
  };
  if (!fechaIso) return empty;
  const d = parseCalendarDate(fechaIso);
  if (!d || isNaN(d.getTime())) return empty;

  const dia = d.getDate();
  const mes = d.getMonth() + 1;
  const anio = d.getFullYear();
  const diaSemana = DIAS_SEMANA[d.getDay()];
  const mesNombre = MESES[d.getMonth()];
  const mm = mes.toString().padStart(2, '0');
  const dd = dia.toString().padStart(2, '0');

  return {
    valida: true,
    dia, mes, anio,
    mesNombre,
    diaSemana,
    fechaNumerica: `${dd} · ${mm} · ${anio}`,
    fechaLarga: `${diaSemana} ${dia} de ${mesNombre} de ${anio}`,
    // `wedding_date` es date-only: no tiene una hora real (las horas de ceremonia
    // viven en campos aparte). Se deja vacío para que el DateBlock del hero no
    // muestre una hora fantasma dependiente del huso del invitado.
    horaEnLetra: '',
    monograma: mono,
  };
}
